/**
 * My own connection: a slim "Reconnecting…" pill at the top while Socket.IO
 * retries (after a short grace period, so a blip doesn't flash), then a
 * "Back online" toast. Controls are disabled meanwhile by the action
 * selectors (`online`), and draft tiles are untouched.
 */
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { useRoomStore } from '../state/roomStore';
import { useUiStore } from '../state/uiStore';
import { IconWifiOff } from './Icons';
import './Banners.css';

const GRACE_MS = 700;

export function ConnectionBanner() {
  const connection = useRoomStore((s) => s.connection);
  const everConnected = useRoomStore((s) => s.everConnected);
  const inRoom = useRoomStore((s) => s.session !== null);
  const offline = connection === 'reconnecting' || (connection === 'connecting' && inRoom);
  const [visible, setVisible] = useState(false);
  const wasShown = useRef(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (offline) {
      const timer = setTimeout(() => {
        setVisible(true);
        wasShown.current = true;
        useUiStore.getState().announce(everConnected ? 'Connection lost. Reconnecting…' : 'Connecting to the server…');
      }, GRACE_MS);
      return () => clearTimeout(timer);
    }
    setVisible(false);
    if (wasShown.current && connection === 'connected') {
      wasShown.current = false;
      useUiStore.getState().toast('Back online', { kind: 'success', id: 'online', duration: 2400 });
    }
    return undefined;
  }, [offline, connection, everConnected]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="conn-banner"
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: -24 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: -24 }}
          // Outside the game's MotionConfig: reduced motion is handled here (a ≤ 120 ms fade).
          transition={reduced ? { duration: 0.12 } : { type: 'spring', stiffness: 420, damping: 32 }}
        >
          {everConnected ? <IconWifiOff /> : <span className="btn__spinner" aria-hidden="true" />}
          <span className="conn-banner__text">
            <strong>{everConnected ? 'Reconnecting…' : 'Connecting…'}</strong>
            {everConnected && <span className="conn-banner__hint"> Your tiles are safe.</span>}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
